import {useEffect, useState} from "react";
import {useAuthStore} from "../../store/auth.store";
import {Coordinates, getCoordinates} from "../../service/map.service";
import {boolean, number, string} from "yup";

interface GridOverlayProps {
    map: google.maps.Map | null,
    stepLat: number,
    stepLng: number,
    mapAdmin: boolean,
    marker?: Coordinates
}

export const GridOverlay = ({mapAdmin, map, stepLat, stepLng, marker}: GridOverlayProps) => {
    const {can} = useAuthStore();
    const [currentDistance, setCurrentDistance] = useState(0);
    const [locations, setLocations] = useState<Coordinates[]>([]);
    const [center, setCenter] = useState<{ lat: number, lng: number }>({lat: -1, lng: -1});
    const [addedMarkers, setAddedMarkers] = useState<Record<number, number>>({});

    useEffect(() => {
        const timeout = setTimeout(() => {
            if ((currentDistance > 0 && mapAdmin && center.lat > -1 && center.lng > -1)) {
                getCoordinates(center.lat, center.lng, currentDistance / 2).then((response) => {
                    setLocations(response.data)
                })
            }
        }, 500)
        return () => clearTimeout(timeout);
    }, [currentDistance, center, mapAdmin])

    useEffect(() => {
        if(!marker) {
            return;
        }
        var lat = Math.round(marker!.lat * 100000) / 100000;
        var lng = Math.round(marker!.lng * 100000) / 100000;
        if(addedMarkers[lat] && addedMarkers[lat] === lng) {
            return;
        }
        setAddedMarkers({...addedMarkers, [lat]: lng});
        const locs = [...locations].filter(l => !(l.lat === lat && l.lng === lng));
        locs.push(marker)
        setLocations(locs);
    }, [marker, addedMarkers, locations]);


    useEffect(() => {
        if (!map) return;
        if (!can('full_access')) {
            return;
        }
        const layer = document.createElement("div");
        layer.style.position = "absolute";
        let listeners: google.maps.MapsEventListener[] = [];

        const overlay = new google.maps.OverlayView();
        overlay.onAdd = function () {

            const projection = this.getProjection();

            const drawGrid = () => {
                if (!projection) {
                    layer.innerHTML = '';
                    return;
                }
                const bounds = map.getBounds();
                if (!bounds) {
                    layer.innerHTML = '';
                    return;
                }
                const zoom = map.getZoom();
                if (zoom && zoom < 15) {
                    layer.innerHTML = '';
                    return;
                }

                if (!mapAdmin) {
                    layer.innerHTML = '';
                    return;
                }
                const ne = bounds.getNorthEast();
                const sw = bounds.getSouthWest();
                const distance = Math.round(google.maps.geometry.spherical.computeDistanceBetween(ne, sw));
                if (mapAdmin && distance !== currentDistance) {
                    setCurrentDistance(distance);
                }
                const currentCenter = bounds.getCenter();
                const currentCenterLat = Math.round(currentCenter.lat() * 100000) / 100000;
                const currentCenterLng = Math.round(currentCenter.lng() * 100000) / 100000;
                if (currentCenterLat !== center.lat || currentCenterLng !== center.lng) {
                    setCenter({lat: currentCenterLat, lng: currentCenterLng})
                }

                const elements: HTMLDivElement[] = [];

                locations.forEach(l => {
                    const div = document.createElement('div');
                    const centerPoint = projection.fromLatLngToDivPixel(
                        new google.maps.LatLng(l.lat, l.lng)
                    )!
                    const EARTH_RADIUS = 6378137; // meters
                    const RADIUS_METERS = 10;

                    // 1 meter in degrees latitude
                    const deltaLat = (RADIUS_METERS / EARTH_RADIUS) * (180 / Math.PI);

                    // 1 meter in degrees longitude (depends on latitude)
                    const deltaLng = (RADIUS_METERS / (EARTH_RADIUS * Math.cos((l.lat * Math.PI) / 180))) * (180 / Math.PI);

                    // Get pixel coordinates for radius distance
                    const edgePoint = projection.fromLatLngToDivPixel(
                        new google.maps.LatLng(l.lat + deltaLat, l.lng + deltaLng)
                    )!;

                    const radiusPx = Math.abs(edgePoint!.x - centerPoint.x);
                    const diameterPx = radiusPx * 2;

                    // Style the div as a circle
                    div.style.position = "absolute";
                    div.style.left = (centerPoint.x - radiusPx) + "px";
                    div.style.top = (centerPoint.y - radiusPx) + "px";
                    div.style.width = diameterPx + "px";
                    div.style.height = diameterPx + "px";
                    div.style.borderRadius = "50%";
                    div.style.border = l.address ? "1px solid rgba(0,0,0,0.2)" : "1px dashed rgba(0,0,0,0.8)";
                    div.style.background = l.address ? '' : "rgba(232,147,147,0.1)";
                    div.style.display = "flex";
                    div.style.alignItems = "center";
                    div.style.justifyContent = "center";
                    div.style.fontSize = "10px";


                    if (zoom! > 18) {
                        div.innerHTML =
                            '<div>' + (Math.round(l.lat * 100000) / 100000) + '/' +
                            (Math.round(l.lng * 100000) / 100000) + " " +
                            (l.address ?  '<br/>' + l.address : '') + '</div>';
                    }

                    elements.push(div);
                })
                /*for (let lat = startLat; lat <= endLat; lat += stepLat) {
                    for (let lng = startLng; lng <= endLng; lng += stepLng) {
                        const p1 = projection.fromLatLngToDivPixel(
                            new google.maps.LatLng(lat, lng)
                        );
                        const p2 = projection.fromLatLngToDivPixel(
                            new google.maps.LatLng(lat + stepLat, lng + stepLng)
                        )
                        if (!p1 || !p2) continue;
                        const div = document.createElement('div');
                        div.style.position = "absolute";
                        div.style.border = '1px solid rgba(0,0,0,0.2)';
                        div.style.left = `${Math.min(p1.x, p2.x)}px`;
                        div.style.top = `${Math.min(p1.y, p2.y)}px`;
                        div.style.width = `${Math.abs(p2.x - p1.x)}px`;
                        div.style.height = `${Math.abs(p2.y - p1.y)}px`;
                        if(zoom! > 20) {
                            div.innerHTML = (Math.round(lat * 10000) / 10000) + '/' + (Math.round(lng * 10000) / 10000);
                        }
                        elements.push(div);
                    }
                }*/
                Array.from(layer.children).forEach(c => c.remove());
                elements.forEach(e => layer.appendChild(e))
            };

            this.getPanes()?.overlayLayer.appendChild(layer);
            overlay.draw = drawGrid;

            listeners.push(map.addListener("bounds_changed", drawGrid));
            listeners.push(map.addListener("zoom_changed", drawGrid));
        };

        overlay.onRemove = function () {
            if (layer && layer.parentNode) {
                layer.parentNode.removeChild(layer);
            }
            listeners.forEach((l) => l.remove());
            listeners = [];
        };

        overlay.setMap(map);

        return () => {
            overlay.setMap(null);
        };
    }, [mapAdmin, map, center, stepLat, stepLng, can, currentDistance, locations]);

    return null;
};